/**
 * Shared font-family helpers used by both the center-design text path
 * (`state.font`, drives the on-canvas text texture and the configurator
 * preview) and the LED-side wall text path (`state.ledTextFont`, drives
 * the perimeter "neon" text band geometry). Earlier each call site had its
 * own near-identical implementation of:
 *
 *   - a list of CSS generic family keywords that must NOT be quoted
 *   - a normaliser that wraps custom families in double quotes (so spaces
 *     and apostrophes survive `ctx.font` / `<link>` URLs)
 *   - a Google Fonts stylesheet loader (de-duped per session)
 *   - a `document.fonts.load(...)` chain to make `CanvasRenderingContext2D`
 *     bake glyphs with the actual webfont rather than a fallback
 *
 * Each version was slightly different (one quoted strings already wrapped
 * in `'…'`, the other always re-wrapped them) which made it possible for
 * `MirrorScene` and `makeTextTexture` to disagree on the same input. Now
 * there is one source of truth.
 */

const GENERIC_FONT_FAMILIES = new Set([
  "serif",
  "sans-serif",
  "monospace",
  "cursive",
  "fantasy",
  "system-ui",
  "ui-serif",
  "ui-sans-serif",
  "ui-monospace",
  "ui-rounded",
  "emoji",
  "math",
  "fangsong",
]);

/**
 * Pull the first non-generic family token out of a CSS family list. Used to
 * derive a Google Fonts URL — generic keywords (`sans-serif`, etc.) and
 * fallbacks must not be requested from the CDN.
 */
export function extractPrimaryFontFamily(font: string): string | null {
  const first = font
    .split(",")
    .map((part) => part.trim())
    .find((part) => part.length > 0);
  if (!first) return null;
  const unquoted = first.replace(/^["']|["']$/g, "").trim();
  if (!unquoted) return null;
  if (GENERIC_FONT_FAMILIES.has(unquoted.toLowerCase())) return null;
  return unquoted;
}

/**
 * CSS-safe family list. Each comma-separated component is unquoted, then
 * generic keywords are kept bare while custom families are double-quoted
 * (with embedded `"` escaped). Empty input returns `"sans-serif"`.
 *
 * Output is suitable for both `ctx.font = "${weight}${size}px ${family}"`
 * and `document.fonts.load("…px ${family}")`.
 */
export function normalizeFontFamilyCss(font: string): string {
  const parts = font
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return "sans-serif";
  return parts
    .map((part) => {
      const unquoted = part.replace(/^["']|["']$/g, "").trim();
      if (!unquoted) return "";
      if (GENERIC_FONT_FAMILIES.has(unquoted.toLowerCase())) return unquoted;
      return `"${unquoted.replace(/"/g, '\\"')}"`;
    })
    .filter(Boolean)
    .join(", ");
}

const fontStylesheetLoads = new Map<string, Promise<void>>();

/**
 * Inject a Google Fonts `<link rel="stylesheet">` for `fontFamily` if not
 * already loaded. Returns a promise that resolves when the stylesheet
 * (`load` or `error`) has settled. De-duplicates by family — concurrent
 * callers share the same promise.
 *
 * `display=swap` is intentional: we accept the FOUT so the configurator can
 * paint with a fallback while the webfont loads, then re-bake the canvas
 * texture in `MirrorScene` when `document.fonts.ready` resolves.
 */
export function ensureGoogleFontStylesheet(fontFamily: string): Promise<void> {
  if (typeof document === "undefined") return Promise.resolve();
  const family = extractPrimaryFontFamily(fontFamily);
  if (!family) return Promise.resolve();
  const id = `viewer-font-${family.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const existingPromise = fontStylesheetLoads.get(id);
  if (existingPromise) return existingPromise;
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (existing?.dataset.viewerLoaded === "1") return Promise.resolve();

  const loadPromise = new Promise<void>((resolve) => {
    const resolveOnce = () => {
      const linkEl = document.getElementById(id) as HTMLLinkElement | null;
      if (linkEl) linkEl.dataset.viewerLoaded = "1";
      resolve();
    };
    if (existing) {
      if (existing.sheet) {
        resolveOnce();
        return;
      }
      existing.addEventListener("load", resolveOnce, { once: true });
      existing.addEventListener("error", resolveOnce, { once: true });
      return;
    }
    const hrefFamily = encodeURIComponent(family).replace(/%20/g, "+");
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${hrefFamily}:wght@400;700&display=swap`;
    link.addEventListener("load", () => {
      resolveOnce();
    });
    link.addEventListener("error", () => {
      resolveOnce();
    });
    document.head.appendChild(link);
  });
  fontStylesheetLoads.set(id, loadPromise);
  return loadPromise;
}

const fontReadyPromises = new Map<string, Promise<void>>();

/**
 * Make sure `fontFamily`'s 400 + 700 weights are decoded and ready to draw
 * into a 2D canvas. Combines the stylesheet load with `document.fonts.load`
 * + `document.fonts.ready` so the next `ctx.fillText` paints actual glyphs
 * rather than the metric fallback. Idempotent — concurrent calls for the
 * same family share one promise; a second call after success resolves
 * synchronously from the cache.
 */
export function ensureFontReady(fontFamily: string): Promise<void> {
  const cached = fontReadyPromises.get(fontFamily);
  if (cached) return cached;
  const promise = (async () => {
    await ensureGoogleFontStylesheet(fontFamily);
    if (typeof document === "undefined" || !document.fonts?.load) return;
    const familyCss = normalizeFontFamilyCss(fontFamily);
    try {
      await Promise.all([
        document.fonts.load(`48px ${familyCss}`),
        document.fonts.load(`400 48px ${familyCss}`),
        document.fonts.load(`700 48px ${familyCss}`),
      ]);
      await document.fonts.ready;
    } catch {
      /* `document.fonts.load` rejects for invalid family strings — fall
         through; the canvas will draw with whatever the browser falls back
         to and a later ConfiguratorState change will retry. */
    }
  })();
  fontReadyPromises.set(fontFamily, promise);
  return promise;
}
